import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CATEGORIES, KIND_NAMES, LAYOUT } from "@/data/affidavits";
import PageFrame from "@/components/PageFrame";
import OwnershipModal from "@/components/OwnershipModal";

// Affidavit picker. Same affidavits and tile images as www.beeregistry.co.za/affidavit.asp, but
// smaller tiles with more space between them, one bordered box per category, and a short note in
// each box explaining EME / QSE and the turnover limits. Normal page scroll, no search.
// Clicking an affidavit first asks for the black ownership (owner), then the company details (Company
// Details page), then opens it filled in.
const KindBadge = ({ kind }) => (
  <span className={`bee-kind bee-kind-${kind.toLowerCase()}`}>{kind}</span>
);

const Selector = () => {
  const navigate = useNavigate();
  const [picked, setPicked] = useState(null); // slug waiting for its black ownership choice

  return (
    <PageFrame title="B-BBEE Sworn Affidavits" subtitle="Choose the affidavit for your business, then sign it online">
      <section className="bee-guide" aria-labelledby="guide-title">
        <h2 id="guide-title">Which affidavit do I need?</h2>
        <ol className="bee-numbered">
          <li>
            <strong>Find your sector:</strong> if your business falls under one of the sector codes below, use that
            sector's affidavit. Otherwise use the <strong>Generic Codes</strong>.
          </li>
          <li>
            <strong>Choose EME or QSE:</strong> use your annual total revenue (turnover) for your latest financial
            year-end. Each sector's limits are shown in its box.
          </li>
        </ol>
        <dl className="bee-guide-terms">
          <div>
            <dt><KindBadge kind="EME" /> {KIND_NAMES.EME}</dt>
            <dd>The smallest businesses. An EME may use an affidavit whatever its level of black ownership.</dd>
          </div>
          <div>
            <dt><KindBadge kind="QSE" /> {KIND_NAMES.QSE}</dt>
            <dd>Larger than an EME. A QSE may only use an affidavit if it is at least 51% black owned.</dd>
          </div>
        </dl>
        <p className="bee-guide-foot">
          * Above the QSE limit (or a QSE that is less than 51% black owned), an affidavit can't be used and a
          B-BBEE verification certificate <strong className="bee-strong-underline">is needed</strong>.
        </p>
      </section>

      {CATEGORIES.map((cat) => (
        <section key={cat.key} className="bee-category" aria-labelledby={`cat-${cat.key}`} data-testid={`category-${cat.key}`}>
          <h2 id={`cat-${cat.key}`} className="bee-category-title">{cat.title}</h2>
          <div className="bee-note">
            {cat.intro.map((text) => (
              <p key={text} className="bee-note-intro">{text}</p>
            ))}
            <ul>
              {cat.limits.map((l, i) => (
                <li key={i}>
                  <KindBadge kind={l.kind} />
                  <span>
                    {l.who && <strong>{l.who}: </strong>}
                    {KIND_NAMES[l.kind]}, {l.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bee-tiles">
            {cat.items.map((item) => (
              <Link
                key={item.slug}
                to={`/affidavit/${item.slug}`}
                className="bee-tile"
                onClick={(e) => { e.preventDefault(); setPicked(item.slug); }}
                data-testid={`tile-${item.slug}`}
                aria-label={`${LAYOUT[item.slug].name} affidavit`}
              >
                <img src={`/affidavits/tiles/${item.slug}.jpg`} alt="" width="480" height="255" loading="lazy" />
                <span className="bee-tile-label">{item.label}</span>
              </Link>
            ))}
          </div>
        </section>
      ))}

      {picked && (
        <OwnershipModal
          slug={picked}
          name={LAYOUT[picked].name}
          onCancel={() => setPicked(null)}
          onDone={(ownership) => navigate(`/affidavit/${picked}/details`, { state: { ownership } })}
        />
      )}
    </PageFrame>
  );
};

export default Selector;
